import React, { useState, useCallback, useEffect } from 'react';
import { User } from 'firebase/auth';
import { auth } from '../firebase/config';
import { AuthContext } from './context';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { loginWithEmail, registerWithEmail, logoutUser, loginWithGoogle } from './services';
import { handleAuthError } from './utils/errors';
import type { AuthContextType } from './types';

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        setUser(user);
        setIsLoading(false);
      },
      (error) => {
        setError(error instanceof Error ? error : new Error('Authentication error'));
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleError = useCallback((error: unknown) => {
    const authError = handleAuthError(error);
    setError(authError);
    throw authError;
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      const user = await loginWithEmail(email, password);
      setUser(user);
    } catch (error) {
      handleError(error);
    }
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      setError(null);
      const user = await registerWithEmail(email, password, name);
      setUser(user);
    } catch (error) {
      handleError(error);
    }
  };

  const loginWithGoogleAuth = async () => {
    try {
      setError(null);
      const user = await loginWithGoogle();
      if (user) {
        setUser(user);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await logoutUser();
      setUser(null);
    } catch (error) {
      handleError(error);
    }
  };

  const clearError = () => setError(null);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    loginWithGoogle: loginWithGoogleAuth,
    register,
    logout,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}